import {
  Card,
  CardContent,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@provenleap/ui";
import { DotsThreeVertical, PencilSimple, Archive, ArrowCounterClockwise, Trash } from "@phosphor-icons/react";
import { ClientAvatar } from "./ClientAvatar";
import { TagPill } from "./TagPill";
import { getIndustryIcon } from "./IndustryPicker";
import { PlatformLogos } from "./PlatformLogos";
import type { Client } from "./types";

interface ClientCardGridProps {
  clients: Client[];
  canManageClients: boolean;
  isArchived?: boolean;
  onClientClick?: (client: Client) => void;
  onEditClick?: (client: Client) => void;
  onArchiveClick?: (client: Client) => void;
  onRestoreClick?: (client: Client) => void;
  onDeleteClick?: (client: Client) => void;
  restoreDisabled?: boolean;
  triggerRefs: React.MutableRefObject<Record<string, HTMLButtonElement | null>>;
}

export function ClientCardGrid({
  clients,
  canManageClients,
  isArchived = false,
  onClientClick,
  onEditClick,
  onArchiveClick,
  onRestoreClick,
  onDeleteClick,
  restoreDisabled = false,
  triggerRefs,
}: ClientCardGridProps) {
  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4${isArchived ? " mt-6" : ""}`}>
      {clients.map((client) => (
        <Card
          key={client.id}
          className={`${isArchived ? "opacity-60" : "shadow-xs hover:shadow-sm transition-shadow"} ${onClientClick ? "cursor-pointer" : ""} !rounded-[22px]`}
          style={{ border: "0.5px solid var(--hairline)" }}
          onClick={() => onClientClick?.(client)}
          role={onClientClick ? "link" : undefined}
          tabIndex={onClientClick ? 0 : undefined}
          onKeyDown={(e) => {
            if (onClientClick && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              onClientClick(client);
            }
          }}
        >
          <CardContent className="p-0">
            <div className="flex items-center gap-3 px-4 pt-4 pb-3">
              <ClientAvatar
                name={client.name}
                logoUrl={client.logoUrl}
                size="sm"
              />
              {isArchived ? (
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="bg-neutral-100 text-neutral-500 text-xs rounded-full px-2 py-0.5 shrink-0">
                    Archived
                  </span>
                  <h3 className="text-[15px] font-medium truncate">{client.name}</h3>
                </div>
              ) : (
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-[14px] font-medium truncate">{client.name}</h3>
                  </div>
                </div>
              )}
              {client.industry && (() => {
                const Icon = getIndustryIcon(client.industry);
                return (
                  <span className="flex items-center gap-1.5 text-sm text-foreground shrink-0">
                    {Icon && <Icon size={16} className="shrink-0 text-foreground" />}
                    {client.industry}
                  </span>
                );
              })()}
            </div>
            <div className="mx-4" style={{ borderTop: "0.5px solid hsl(var(--input) / 0.5)" }} />
            <div className="pt-3 pb-3 px-4 space-y-3">
              {client.platforms && client.platforms.length > 0 && (
                <div className="mb-3">
                  <PlatformLogos platforms={client.platforms} />
                </div>
              )}
              {client.tags && client.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {[...client.tags]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .slice(0, 3)
                    .map((tag) => (
                      <TagPill key={tag.id} tag={tag} size="sm" className="!text-[13px]" />
                    ))}
                  {client.tags.length > 3 && (
                    <span className="text-xs text-muted-foreground px-1.5 py-0.5">
                      +{client.tags.length - 3} more
                    </span>
                  )}
                </div>
              )}
              <div className="flex items-center gap-3 text-[13px] text-foreground -mt-1">
                <span>Created {new Date(client.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                <span className="w-px h-3.5" style={{ background: "hsl(var(--input) / 0.3)" }} />
                <span>Updated {new Date(client.updatedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                {canManageClients && (
                  <div className="ml-auto" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        ref={(el) => {
                          triggerRefs.current[client.id] = el;
                        }}
                        variant="ghost"
                        size="icon"
                        className="min-h-8 min-w-8 cursor-pointer rounded-lg border border-transparent hover:bg-background hover:border-input transition-all"
                        aria-label={`Actions for ${client.name}`}
                      >
                        <DotsThreeVertical size={22} weight="bold" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-xl p-2 shadow-md" style={{ border: "0.5px solid var(--hairline)" }}>
                      {isArchived ? (
                        <DropdownMenuItem
                          className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                          onClick={() => onRestoreClick?.(client)}
                          disabled={restoreDisabled}
                        >
                          <ArrowCounterClockwise size={14} className="mr-1" />
                          Restore
                        </DropdownMenuItem>
                      ) : (
                        <>
                          <DropdownMenuItem
                            className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                            onClick={() => onEditClick?.(client)}
                          >
                            <PencilSimple size={14} className="mr-1" />
                            Edit Client
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                            onClick={() => onArchiveClick?.(client)}
                          >
                            <Archive size={14} className="mr-1" />
                            Archive
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="min-h-11 text-[13px] text-destructive cursor-pointer rounded-lg hover:!bg-destructive/10 focus:!bg-destructive/10 focus:!text-destructive focus:!border-destructive/30"
                        onClick={() => onDeleteClick?.(client)}
                      >
                        <Trash size={14} className="mr-1" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
